import React, { createContext, useState, useEffect, useContext } from 'react';
import userService from '../services/userService';
import { useToast } from '@chakra-ui/react';

// Define wallet context interface
interface WalletContextType {
  walletAddress: string | null;
  userBalance: number;
  connectWallet: (address?: string) => void;
  disconnectWallet: () => void;
  addBalance: (amount: number) => Promise<void>;
  refreshBalance: () => Promise<void>;
  isLoading: boolean;
}

// Create the context
const WalletContext = createContext<WalletContextType | undefined>(undefined);

// Create a provider component
export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState<string | null>(localStorage.getItem('walletAddress'));
  const [userBalance, setUserBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const toast = useToast();

  // Initialize user if wallet is connected
  useEffect(() => {
    if (walletAddress) {
      initializeUser(walletAddress);
    }
  }, [walletAddress]);

  // Initialize or get existing user
  const initializeUser = async (address: string) => {
    try {
      setIsLoading(true);
      const user = await userService.getOrCreateUser(address);
      setUserBalance(user.balance);
    } catch (error) {
      console.error('Error initializing user:', error);
      toast({
        title: 'Error',
        description: "Failed to initialize user account",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Connect wallet
  const connectWallet = (address?: string) => {
    const generatedAddress = address || generateRandomAddress();
    localStorage.setItem('walletAddress', generatedAddress);
    setWalletAddress(generatedAddress);
    
    toast({
      title: 'Wallet Connected',
      description: `Connected to wallet ${generatedAddress.substring(0, 6)}...${generatedAddress.substring(generatedAddress.length - 4)}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    localStorage.removeItem('walletAddress');
    setWalletAddress(null);
    setUserBalance(0);
    
    toast({
      title: 'Wallet Disconnected',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Add balance (for demo purposes)
  const addBalance = async (amount: number) => {
    if (!walletAddress) return;

    try {
      setIsLoading(true);
      const user = await userService.addBalance(walletAddress, amount);
      setUserBalance(user.balance);
      
      toast({
        title: 'Balance Added',
        description: `${amount} SOL has been added to your wallet`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error adding balance:', error);
      toast({
        title: 'Error',
        description: "Failed to add balance to your wallet",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh user balance
  const refreshBalance = async () => {
    if (!walletAddress) return;

    try {
      setIsLoading(true);
      const user = await userService.getUser(walletAddress);
      setUserBalance(user.balance);
    } catch (error) {
      console.error('Error refreshing balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a random wallet address for demo
  const generateRandomAddress = () => {
    return `${Math.random().toString(36).substring(2, 10)}${Math.random().toString(36).substring(2, 10)}`;
  };

  return (
    <WalletContext.Provider
      value={{
        walletAddress,
        userBalance,
        connectWallet,
        disconnectWallet,
        addBalance,
        refreshBalance,
        isLoading
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

// Custom hook to use the wallet context
export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}; 